import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Building2, Pencil, Shield, Trash2, User, Users, type LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import InitialsAvatar from '@/components/common/InitialsAvatar';
import { PERMISSION_RIGHT_BADGES } from '@/constants/permissions';
import type { Permission, PermissionTargetType } from '@/types/permissions';
import { cn } from '@/lib/utils';

export interface PermissionTableProps {
  permissions: Permission[];
  onEdit: (permission: Permission) => void;
  onDelete: (permission: Permission) => void;
}

const ENTITY_ICONS: Record<PermissionTargetType, LucideIcon> = {
  user: User,
  group: Users,
  tenant: Building2,
};

const entityIcon = (type: PermissionTargetType) => {
  const Icon = ENTITY_ICONS[type] ?? Shield;
  return <Icon className="mx-auto size-4" strokeWidth={1.8} aria-hidden />;
};

const PermissionTable: React.FC<PermissionTableProps> = ({ permissions, onEdit, onDelete }) => {
  const { t } = useTranslation();

  const columns = React.useMemo<ColumnDef<Permission, any>[]>(
    () => [
      {
        id: 'entity',
        accessorKey: 'targetName',
        header: t('permissions.entity'),
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <InitialsAvatar fallback={entityIcon(row.original.type)} />
            <span className="revy-cjk text-sm leading-[1.5]">{row.original.targetName}</span>
          </div>
        ),
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: t('permissions.type'),
        cell: ({ row }) => (
          <Badge variant="outline" className="revy-cjk font-normal leading-[1.5]">
            {t(`permissions.types.${row.original.type}`)}
          </Badge>
        ),
      },
      {
        id: 'rights',
        header: t('documents.permissions'),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {PERMISSION_RIGHT_BADGES.filter((right) => row.original[right.key]).map((right) => (
              <Badge
                key={right.key}
                variant="outline"
                className={cn('revy-cjk font-normal leading-[1.5]', right.badgeClassName)}
              >
                {t(right.labelKey)}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        id: 'grantedBy',
        accessorKey: 'grantedBy',
        header: t('permissions.grantedBy'),
        cell: ({ row }) => (
          <span className="revy-cjk text-sm leading-[1.5]">{row.original.grantedBy}</span>
        ),
      },
      {
        id: 'grantedAt',
        accessorKey: 'grantedAt',
        header: t('permissions.grantedDate'),
        cell: ({ row }) => (
          <span className="revy-figure text-sm">
            {new Date(row.original.grantedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="block text-right">{t('common.actions')}</span>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-0.5">
            <TooltipIconButton
              size="icon-sm"
              label={t('common.edit')}
              icon={<Pencil strokeWidth={1.8} />}
              onClick={() => onEdit(row.original)}
            />
            <TooltipIconButton
              size="icon-sm"
              label={t('common.delete')}
              icon={<Trash2 strokeWidth={1.8} />}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [t, onEdit, onDelete],
  );

  return (
    <DataTable
      paginated
      columns={columns}
      data={permissions}
      getRowId={(row) => row.id}
    />
  );
};

export default PermissionTable;
